import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { toast } from "react-toastify";
import MessageLoading from "../components/pages/chat/MessageLoading";
import MessageRenderer from "../components/pages/chat/MessageRenderer";
import ChatSidebar from "../components/pages/chat/ChatSidebar";
import ChatHeader from "../components/pages/chat/ChatHeader";
import RightPanel from "../components/pages/chat/right-panel/RightPanel";
import PromptInput from "../components/pages/chat/PromptInput";
import PromptSuggestions from "../components/pages/chat/PromptSuggestions";
import { useChat } from "../hooks/useChat";
import { useChatScroll } from "../hooks/useChatScroll";
import {
  getChatHistory,
  getThreads,
  createThread,
  regenerateMessageStream,
  generateImage,
} from "../api/apiFunction/chatService";
import {
  Copy,
  Check,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Pencil,
} from "lucide-react";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import ModelDropdown from "../components/pages/chat/ModelDropdown";
import RegenerateModelsDropdown from "../components/pages/chat/RegenerateModelsDropdown";
// Removed plan upgrade modal imports per new flow
import ErrorModal from "../components/pages/chat/ErrorModal";
import DownloadResponse from "../components/pages/chat/DownloadResponse";
import TextToSpeech from "../components/pages/chat/TextToSpeech";
import { isRTL } from "../utils/textUtils";
import { updatePageTitle } from "../utils/titleUtils";
import ToolsLanding from "../components/pages/chat/ToolsLanding";
import ImageModal from "../components/pages/chat/ImageModal";
import { downloadImage, getImageFileName } from "../utils/imageUtils";
import SettingsModal from "../components/pages/settings/SettingsModal";
import { useAnonymousSession } from "../hooks/useAnonymousSession";
import UpgradePrompt from "../components/UpgradePrompt";
import SubscriptionStatusBanner from "../components/SubscriptionStatusBanner";
import { viewLogs } from "../utils/viewLogs";
import * as Sentry from "@sentry/react";

const { logger } = Sentry;

// Make viewLogs available globally for debugging
if (typeof window !== "undefined") {
  window.viewLogs = viewLogs;
}

const Chat = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { threadId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [currentThreadId, setCurrentThreadId] = useState(threadId || null);
  const {
    sendMessage: sendChatMessage,
    getAvailableModelsForDropdown,
    getDefaultModelId,
    isChatLoading,
    error: chatError,
    stopGenerating,
  } = useChat();
  const [prompt, setPrompt] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [isUsingFallbackModel, setIsUsingFallbackModel] = useState(false); // Track if fallback model is active
  const [models, setModels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  const [chatThreads, setChatThreads] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef(null);
  const [showRegenerateDropdown, setShowRegenerateDropdown] = useState(null);
  const [regenerateDropdownPosition, setRegenerateDropdownPosition] =
    useState("bottom");
  const regenerateDropdownRef = useRef(null);
  const regenerateButtonRefs = useRef({});
  // Plan upgrade flow removed; users upgrade themselves now

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [showToolsLanding, setShowToolsLanding] = useState(false);

  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");
  const [modalImageAlt, setModalImageAlt] = useState("");
  const [modalMessageId, setModalMessageId] = useState("");

  // Persona state
  const [selectedPersona, setSelectedPersona] = useState(null);

  // Right panel state
  const [rightPanelExpanded, setRightPanelExpanded] = useState(false);
  const [activeRightPanel, setActiveRightPanel] = useState("models"); // 'models' | 'youtube' | 'research'

  // Model settings state
  const [temperature, setTemperature] = useState(0.7);
  const [topP, setTopP] = useState(1.0);

  // Word-based streaming system (like mobile app)
  const wordQueues = useRef({});
  const wordTickers = useRef({});
  
  const smartTokenize = useCallback((text) => {
    if (!text) return [];
    
    // Split on spaces but preserve spaces as separate tokens
    const tokens = [];
    const parts = text.split(/(\s+)/);
    
    for (const part of parts) {
      if (part.length > 0) {
        tokens.push(part);
      }
    }
    
    return tokens;
  }, []);
  
  const addToWordQueue = useCallback((messageId, chunk) => {
    if (!chunk) return;
    
    // Initialize queue if doesn't exist
    if (!wordQueues.current[messageId]) {
      wordQueues.current[messageId] = [];
    }
    
    // Tokenize chunk and add to queue
    const tokens = smartTokenize(chunk);
    console.log('✅ ADDING TO QUEUE:', { messageId, chunkPreview: chunk.slice(0, 50) + '...', tokens: tokens.length });
    wordQueues.current[messageId].push(...tokens);
    
    // Start ticker if not already running
    if (!wordTickers.current[messageId]) {
      startWordTicker(messageId);
    }
  }, []);
  
  const startWordTicker = useCallback((messageId) => {
    wordTickers.current[messageId] = setInterval(() => {
      const queue = wordQueues.current[messageId];
      if (!queue || queue.length === 0) {
        // Stop ticker when queue is empty
        if (wordTickers.current[messageId]) {
          clearInterval(wordTickers.current[messageId]);
          wordTickers.current[messageId] = null;
        }
        return;
      }
      
      // Take 3-4 words at a time for faster streaming
      let wordsToAdd = '';
      for (let i = 0; i < 4 && queue.length > 0; i++) {
        wordsToAdd += queue.shift();
      }
      
      if (wordsToAdd) {
        // Update message content
        setMessages(prev => prev.map(msg => 
          msg.id === messageId 
            ? { ...msg, content: (msg.content || '') + wordsToAdd }
            : msg
        ));
      }
    }, 40); // Faster interval: 40ms (25 FPS) for quicker streaming
  }, []);
  
  // Cleanup function
  const cleanupWordTicker = useCallback((messageId) => {
    if (wordTickers.current[messageId]) {
      clearInterval(wordTickers.current[messageId]);
      wordTickers.current[messageId] = null;
    }
    if (wordQueues.current[messageId]) {
      delete wordQueues.current[messageId];
    }
  }, []);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(wordTickers.current).forEach(ticker => {
        if (ticker) clearInterval(ticker);
      });
    };
  }, []);

  // Anonymous session tracking
  const {
    isAnonymous,
    messageCount,
    remainingMessages,
    isLimitReached,
    hoursUntilReset,
    dailyLimit,
    handleLimitReached,
    incrementMessageCount,
  } = useAnonymousSession();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  
  // Subscription status state
  const [subscription, setSubscription] = useState(null);
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(false);

  // Check subscription status on mount and when user data changes
  useEffect(() => {
    const checkSubscriptionStatus = () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        
        if (userData && userData.token && userData.subscription) {
          const sub = userData.subscription;
          
          // Check if subscription is past_due or canceled
          if (sub.status === 'past_due' || sub.status === 'canceled') {
            setSubscription(sub);
            setShowSubscriptionBanner(true);
            logger.info('[Chat] Subscription status issue detected', {
              status: sub.status,
              subscriptionId: sub._id
            });
          } else {
            setSubscription(null);
            setShowSubscriptionBanner(false);
          }
        } else {
          setSubscription(null);
          setShowSubscriptionBanner(false);
        }
      } catch (error) {
        logger.error('[Chat] Error checking subscription status', { error: error.message });
        setSubscription(null);
        setShowSubscriptionBanner(false);
      }
    };

    checkSubscriptionStatus();
    
    // Also check when localStorage changes (e.g., after login)
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        checkSubscriptionStatus();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Check periodically in case user data is updated in same tab
    const interval = setInterval(checkSubscriptionStatus, 5000);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Log when Chat component mounts
  useEffect(() => {
    // Use persistent logging
    try {
      const LOG_KEY = "leebai_debug_log";
      const existingLogs = JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
      const logEntry = {
        level: "log",
        category: "Chat",
        message: "🚀 Component mounted",
        data: {
          pathname: location.pathname,
          threadId: currentThreadId,
          isAnonymous,
          hasUser: !!userData?._id,
        },
        timestamp: new Date().toISOString(),
        pathname: window.location.pathname,
      };
      existingLogs.push(logEntry);
      if (existingLogs.length > 100) existingLogs.shift();
      localStorage.setItem(LOG_KEY, JSON.stringify(existingLogs));
      
      logger.info('[Chat] Component mounted', logEntry.data);
    } catch (e) {
      logger.info('[Chat] Component mounted');
    }
    
    // Check if user is anonymous
    const userDataCheck = JSON.parse(localStorage.getItem("user") || "{}");
    if (!userDataCheck || !userDataCheck.token) {
      logger.info('[Chat] Anonymous user detected - chat should work');
    } else {
      logger.info('[Chat] Authenticated user', {
        userId: userDataCheck._id,
        role: userDataCheck.role
      });
    }

    // Monitor window.location changes to catch redirects
    let lastLocation = window.location.href;
    const locationCheckInterval = setInterval(() => {
      if (window.location.href !== lastLocation) {
        if (window.location.pathname.includes('/login') && location.pathname.startsWith('/chat')) {
          const userDataCheck = JSON.parse(localStorage.getItem("user") || "{}");
          if (!userDataCheck || !userDataCheck.token) {
            logger.error('[Chat] REDIRECT DETECTED: Page redirected to login from chat!', {
              from: lastLocation,
              to: window.location.href,
              timestamp: new Date().toISOString()
            });
          }
        }
        lastLocation = window.location.href;
      }
    }, 100);

    return () => {
      clearInterval(locationCheckInterval);
    };
  }, [location.pathname, currentThreadId, isAnonymous]);

  // Fetch models on mount - allow for anonymous users
  // Use a ref to track if we've initialized to avoid re-running when selectedModel changes
  const hasInitializedModels = useRef(false);
  useEffect(() => {
    const fetchModels = async () => {
      try {
        logger.info('[Chat] Fetching models...');
        const fetchedModels = await getAvailableModelsForDropdown();
        logger.info('[Chat] Models fetched successfully', {
          count: fetchedModels?.length || 0
        });
        setModels(fetchedModels || []);
        
        // Set default model only on initial load (when selectedModel is null/undefined)
        // Don't set default if selectedModel is empty string (which means "Default/Auto" was explicitly selected)
        // Only run this logic once on initial mount
        if (!hasInitializedModels.current && (selectedModel === null || selectedModel === undefined) && fetchedModels?.length > 0) {
          hasInitializedModels.current = true;
          try {
            const defaultModelId = await getDefaultModelId();
            logger.info('[Chat] Setting default model', { defaultModelId });
            if (defaultModelId) {
              setSelectedModel(defaultModelId);
            } else if (fetchedModels[0]?._id) {
              setSelectedModel(fetchedModels[0]._id);
            }
          } catch (error) {
            logger.warn('[Chat] Could not get default model ID, using first model', { error: error.message });
            if (fetchedModels[0]?._id) {
              setSelectedModel(fetchedModels[0]._id);
            }
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        logger.error('[Chat] Error fetching models', {
          error: error.message,
          status: error.response?.status
        });
        // Don't block chat if models fail to load - set empty array
        // This is especially important for anonymous users
        logger.warn('[Chat] Continuing without models - chat will still work');
        setModels([]);
      }
    };

    fetchModels();
  }, [getAvailableModelsForDropdown, getDefaultModelId]);

  // Handle limit reached errors
  useEffect(() => {
    if (chatError) {
      const errorData = chatError.response?.data || chatError;
      if (
        errorData.code === "DAILY_LIMIT_REACHED" ||
        errorData.code === "ANONYMOUS_FILE_UPLOAD_BLOCKED" ||
        errorData.code === "ANONYMOUS_ADVANCED_TOOL_BLOCKED"
      ) {
        handleLimitReached(errorData);
        setShowUpgradePrompt(true);
        toast.error(
          errorData.error ||
            "Daily limit reached. Please create an account to continue."
        );
      }
    }
  }, [chatError, handleLimitReached]);

  // Show upgrade prompt if limit is reached
  useEffect(() => {
    if (isLimitReached && isAnonymous) {
      setShowUpgradePrompt(true);
    }
  }, [isLimitReached, isAnonymous]);
  const [instructions, setInstructions] = useState("");
  const [outputLength, setOutputLength] = useState(2000);

  // YouTube settings state - matching YoutubeToolSetup component expectations
  const [youtubeActiveTab, setYoutubeActiveTab] = useState("summarize"); // 'summarize' | 'transcribe'
  const [youtubeSummaryType, setYoutubeSummaryType] = useState("short"); // 'short' | 'medium' | 'long' | 'bullets' | 'chapters'
  const [youtubeLanguage, setYoutubeLanguage] = useState("English");
  const [youtubeTone, setYoutubeTone] = useState("concise"); // 'concise' | 'friendly' | 'technical'

  // Deep Research settings state
  const [researchMode, setResearchMode] = useState("simple"); // 'simple' | 'research' | 'deep' | 'compare'
  const [citationStyle, setCitationStyle] = useState("APA");
  const [includeWebSources, setIncludeWebSources] = useState(true);
  const [includeDocSources, setIncludeDocSources] = useState(true);
  const [includeAIReasoning, setIncludeAIReasoning] = useState(true);
  const [isResearchProcessing, setIsResearchProcessing] = useState(false);

  // Compute all references from thread messages
  const allReferences = React.useMemo(() => {
    const refs = [];
    const seenUrls = new Set();

    messages.forEach((message) => {
      if (message.citations && Array.isArray(message.citations)) {
        message.citations.forEach((citation) => {
          // Avoid duplicates based on URL
          if (!seenUrls.has(citation.url)) {
            seenUrls.add(citation.url);
            refs.push({
              title: citation.title || citation.url,
              url: citation.url,
              type: citation.validation?.domain?.includes("wikipedia")
                ? "doc"
                : "web",
              source: citation.validation?.domain || "Unknown",
              validation: citation.validation,
              messageId: message.id,
            });
          }
        });
      }
    });

    return refs;
  }, [messages]);

  // CHAT HOOK - Initialize chat scroll management with messages dependency
  const { chatContainerRef, handleScroll, smoothScrollToBottom } =
    useChatScroll(messages);

  // Cache for loaded messages to avoid refetching
  const messagesCache = useRef(new Map());

  // Track current thread ID with ref to avoid stale closures
  const currentThreadIdRef = useRef(currentThreadId);

  // Keep ref in sync with state
  useEffect(() => {
    currentThreadIdRef.current = currentThreadId;
  }, [currentThreadId]);

  // Use a ref to always get the latest messages state in async functions
  const messagesRef = useRef(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Helper to get organizationId for all user types
  const getOrganizationId = (userData) => {
    return userData?.organization?._id || userData?.organizationId || undefined;
  };

  // Helper to format messages from backend or cache
  const formatMessagesFromBackend = useCallback((chatData) => {
    return chatData.messages.map((message, index) => ({
      id: `${chatData.threadId || chatData._id}-${index}`,
      mongoId: message._id, // Always set this!
      type: message.sender === "user" ? "user" : "assistant",
      content: message.content,
      reasoning: message.reasoning || "", // Persisted streamed reasoning
      timestamp: new Date(message.timestamp).toLocaleString(),
      attachments: message.attachments || [],
      modelTitle: message.modelTitle,
      citations: message.citations || [], // Include citations from history API
    }));
  }, []);

  // Define fetchAndSyncHistory first
  const fetchAndSyncHistory = useCallback(
    async (userId, organizationId, threadIdToUse, localImageUrl, preserveStreamedContent = false) => {
      try {
        if (userId && threadIdToUse) {
          // Fetch the latest chat history
          const historyResponse = await getChatHistory(
            userId,
            organizationId,
            threadIdToUse
          );
          if (historyResponse?.data?.messages?.length > 0) {
            const chatData = historyResponse.data;
            const formattedMessages = formatMessagesFromBackend(chatData);
            
            // If preserveStreamedContent is true, keep the streamed content but sync metadata
            setMessages((prev) => {
              if (preserveStreamedContent && prev.length > 0) {
                // Preserve the last assistant message content if it was streamed
                const lastPrevMsg = prev[prev.length - 1];
                const lastBackendMsg = formattedMessages[formattedMessages.length - 1];
                
                if (lastPrevMsg && lastPrevMsg.type === "assistant" && 
                    lastBackendMsg && lastBackendMsg.type === "assistant" &&
                    lastPrevMsg.content && lastPrevMsg.content.trim()) {
                  
                  console.log('🔄 Preserving streamed content, syncing metadata only');
                  // Keep the streamed content but update metadata from backend
                  const preservedMessages = [...formattedMessages];
                  preservedMessages[preservedMessages.length - 1] = {
                    ...lastBackendMsg,
                    content: lastPrevMsg.content, // Keep streamed content
                    citations: lastBackendMsg.citations || [], // But get citations from backend
                    modelTitle: lastBackendMsg.modelTitle, // And model info
                  };
                  
                  messagesCache.current.set(
                    chatData.threadId || chatData._id,
                    preservedMessages
                  );
                  return preservedMessages;
                }
              }
              
              // Default behavior - full replacement
              let wasTruncated = false;
              if (prev.length > 0) {
                const lastPrev = prev[prev.length - 1];
                if (lastPrev.type === "assistant" && lastPrev.truncated) {
                  wasTruncated = true;
                }
              }
              if (wasTruncated && formattedMessages.length > 0) {
                // Find the last assistant message in formattedMessages
                for (let i = formattedMessages.length - 1; i >= 0; i--) {
                  if (formattedMessages[i].type === "assistant") {
                    formattedMessages[i].truncated = true;
                    break;
                  }
                }
              }

              // Only preserve error messages - they are local-only and should persist
              const errorMessages = prev.filter((msg) => msg.type === "error");
              const finalMessages = [...formattedMessages, ...errorMessages];

              messagesCache.current.set(
                chatData.threadId || chatData._id,
                formattedMessages
              );
              return finalMessages;
            });
            // Check if the last assistant message has "Default" in modelTitle (fallback indicator)
            // This ensures the header updates even if the SSE event was missed
            // Do this AFTER setMessages to avoid state update issues
            const lastAssistantMessage = formattedMessages
              .slice()
              .reverse()
              .find((msg) => msg.type === "assistant");
            if (lastAssistantMessage && lastAssistantMessage.modelTitle && 
                (lastAssistantMessage.modelTitle === "Default" || lastAssistantMessage.modelTitle.includes("(Default)"))) {
              logger.debug("[Frontend] Detected 'Default' in synced message modelTitle - setting isUsingFallbackModel to true", { modelTitle: lastAssistantMessage.modelTitle });
              setIsUsingFallbackModel(true);
            }
            // Update thread name in chatThreads if needed
            if (chatData.name && chatData.threadId) {
              setChatThreads((prevThreads) =>
                prevThreads.map((thread) =>
                  thread.threadId === chatData.threadId
                    ? { ...thread, name: chatData.name }
                    : thread
                )
              );
              // Update page title with the updated thread name
              updatePageTitle(chatData.name);
            }
            if (localImageUrl) {
              URL.revokeObjectURL(localImageUrl);
            }
          }
        }
      } catch (err) {
        Sentry.captureException(err);
        logger.error("Failed to fetch updated chat history after sending message", { error: err.message });
      }
    },
    [setMessages, setChatThreads, formatMessagesFromBackend, messagesCache]
  );

  // Handle thread selection from history - optimized with useCallback
  const handleChatSelect = useCallback(
    (thread) => {
      const threadId = thread.threadId || thread._id;

      if (!threadId) return;

      // Update the URL and current thread ID
      setCurrentThreadId(threadId);
      navigate(`/chat/${threadId}`, { replace: true });

      // Update page title with thread name
      const threadName = thread.name || "Untitled Chat";
      updatePageTitle(threadName);

      // Close sidebar on mobile for better UX
      if (window.innerWidth < 768) {
        setSidebarExpanded(false);
      }

      // Show cached messages immediately if available
      if (messagesCache.current.has(threadId)) {
        const cached = messagesCache.current.get(threadId) || [];
        // Use cached messages directly
        setMessages(cached);
        setIsLoadingHistory(false);
        return; // Exit early if we have cached messages
      }

      // Show loading state only if we don't have cached messages
      setIsLoadingHistory(true);

      // Load messages in the background
      const loadThreadMessages = async () => {
        let shouldUpdate = true;

        try {
          const userData = JSON.parse(localStorage.getItem("user") || "{}");
          const userId = userData?._id;
          const organizationId = getOrganizationId(userData);

          if (!userId) return;

          // 2. History API: called when a thread is selected from the sidebar/history
          const response = await getChatHistory(
            userId,
            organizationId,
            threadId
          );

          // Check if we should still update (thread hasn't changed)
          shouldUpdate = currentThreadIdRef.current === threadId;
          if (!shouldUpdate) return;

          if (response?.data?.messages?.length > 0) {
            const chatData = response.data;
            const formattedMessages = formatMessagesFromBackend(chatData);
            setMessages(() => {
              messagesCache.current.set(
                chatData.threadId || chatData._id,
                formattedMessages
              );
              return formattedMessages;
            });
            // Check if the last assistant message has "Default" in modelTitle (fallback indicator)
            // This ensures the header updates even if the SSE event was missed
            // Do this AFTER setMessages to avoid state update issues
            const lastAssistantMessage = formattedMessages
              .slice()
              .reverse()
              .find((msg) => msg.type === "assistant");
            if (lastAssistantMessage && lastAssistantMessage.modelTitle && 
                (lastAssistantMessage.modelTitle === "Default" || lastAssistantMessage.modelTitle.includes("(Default)"))) {
              logger.debug("[Frontend] Detected 'Default' in loaded message modelTitle - setting isUsingFallbackModel to true", { modelTitle: lastAssistantMessage.modelTitle });
              setIsUsingFallbackModel(true);
            }
            // Update thread name in chatThreads if needed
            if (chatData.name && chatData.threadId) {
              setChatThreads((prevThreads) =>
                prevThreads.map((thread) =>
                  thread.threadId === chatData.threadId
                    ? { ...thread, name: chatData.name }
                    : thread
                )
              );
              // Update page title with the actual thread name from backend
              updatePageTitle(chatData.name);
            } else {
              // If no name from backend, use a default name
              updatePageTitle("Untitled Chat");
            }
            // Smoothly scroll to bottom after history loads (restores previous animation feel)
            setTimeout(() => {
              if (
                chatContainerRef.current &&
                currentThreadIdRef.current === threadId
              ) {
                smoothScrollToBottom(800);
              }
            }, 50);
          } else {
            setMessages([]);
          }
        } catch (error) {
          Sentry.captureException(error);
          logger.error("Error loading thread messages", { error: error.message });
          toast.error("Failed to load chat messages");
        } finally {
          if (shouldUpdate) {
            setIsLoadingHistory(false);
          }
        }
      };

      loadThreadMessages();
    },
    [navigate, formatMessagesFromBackend]
  );

  // Fetch all threads on component mount and when organization changes
  useEffect(() => {
    const fetchInitialThreads = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = userData?._id;
        const organizationId = getOrganizationId(userData);

        if (userId) {
          // 5. Threads API: called to refresh the thread list (manual refresh or after thread changes)
          const response = await getThreads(userId, organizationId);
          if (Array.isArray(response?.data)) {
            setChatThreads(response.data);
          } else if (response?.data?.threads) {
            setChatThreads(response.data.threads);
          } else {
            setChatThreads([]);
          }
        }
      } catch (error) {
        Sentry.captureException(error);
        logger.error("Error fetching initial threads", { error: error.message });
      }
    };

    fetchInitialThreads();
  }, []);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");

  // Add state to track liked/disliked messages
  const [likedMessages, setLikedMessages] = useState({}); // { [messageId]: 'like' | 'dislike' }

  // Handle URL parameters for opening settings modal with specific tab
  useEffect(() => {
    const openSettings = searchParams.get("openSettings");
    const settingsTab = searchParams.get("tab");

    if (openSettings === "true") {
      setShowSettingsModal(true);
      // Clean up URL by removing the parameters
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, navigate, location.pathname]);

  // Watch messages for "Default" in modelTitle and update header accordingly
  useEffect(() => {
    // Find the last assistant message
    const lastAssistantMessage = messages
      .slice()
      .reverse()
      .find((msg) => msg.type === "assistant");

    if (lastAssistantMessage && lastAssistantMessage.modelTitle) {
      // Check if modelTitle contains "(Default)" or is exactly "Default"
      const hasDefault =
        lastAssistantMessage.modelTitle === "Default" ||
        lastAssistantMessage.modelTitle.includes("(Default)");

      if (hasDefault) {
        // Only set if not already set to avoid unnecessary updates
        setIsUsingFallbackModel((prev) => {
          if (!prev) {
            logger.debug("[Frontend] useEffect: Detected 'Default' in message modelTitle - setting isUsingFallbackModel to true", { modelTitle: lastAssistantMessage.modelTitle });
            return true;
          }
          return prev;
        });
      } else {
        // Reset if the last message doesn't have Default (user sent a new message with different model)
        setIsUsingFallbackModel((prev) => {
          if (prev) {
            logger.debug("[Frontend] useEffect: Last message doesn't have Default, resetting isUsingFallbackModel");
            return false;
          }
          return prev;
        });
      }
    }
  }, [messages, setIsUsingFallbackModel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      ) {
        setShowProfileDropdown(false);
      }
      if (
        regenerateDropdownRef.current &&
        !regenerateDropdownRef.current.contains(event.target)
      ) {
        setShowRegenerateDropdown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    navigate("/login");
  };

  // Right panel handlers
  const toggleRightPanel = () => {
    setRightPanelExpanded(!rightPanelExpanded);
  };

  const handleModelReset = () => {
    setTemperature(0.7);
    setTopP(1.0);
    setInstructions("");
    setOutputLength(2000);
  };

  const handleYoutubeTranscribe = (data) => {
    logger.debug("YouTube transcribe", { data });
    // TODO: Implement YouTube transcribe functionality
  };

  const handleYoutubeSummarize = (data) => {
    logger.debug("YouTube summarize", { data });
    // TODO: Implement YouTube summarize functionality
  };

  // YouTube settings callback handlers
  const handleYoutubeActiveTabChange = (tab) => {
    setYoutubeActiveTab(tab);
  };

  const handleYoutubeSummaryTypeChange = (type) => {
    setYoutubeSummaryType(type);
  };

  const handleYoutubeLanguageChange = (language) => {
    setYoutubeLanguage(language);
  };

  const handleYoutubeToneChange = (tone) => {
    setYoutubeTone(tone);
  };

  // Deep Research handlers
  const handleResearch = (data) => {
    logger.debug("Deep research", { data });
    // Research functionality is handled through the normal chat flow
    // when activeTool is set to "research"
  };

  const handleResearchModeChange = (mode) => {
    setResearchMode(mode);
  };

  const handleCitationStyleChange = (style) => {
    setCitationStyle(style);
  };

  const handleIncludeWebSourcesChange = (include) => {
    setIncludeWebSources(include);
  };

  const handleIncludeDocSourcesChange = (include) => {
    setIncludeDocSources(include);
  };

  const handleIncludeAIReasoningChange = (include) => {
    setIncludeAIReasoning(include);
  };

  const handleSaveReference = (reference) => {
    logger.debug("Save reference", { reference });
    // TODO: Implement save reference functionality
  };

  // Handle copying message text to clipboard
  const copyToClipboard = (text, messageId) => {
    navigator.clipboard.writeText(text);
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000); // Reset after 2 seconds
  };

  // Handle model selection
  const handleModelSelect = (value) => {
    setSelectedModel(value);
    // Reset fallback state when user manually selects a model
    setIsUsingFallbackModel(false);
    logger.debug("[Frontend] Model manually selected, resetting fallback state");

    // Check if the selected model is an image model and trigger image generation
    const selectedModelData = models.find((model) => model._id === value);
    if (selectedModelData && selectedModelData.modelType === "image") {
      // Trigger image generation by setting activeTool to 'image'
      setActiveTool("image");
    } else if (selectedModelData && selectedModelData.modelType !== "image") {
      // Only clear activeTool if switching to a non-image model AND current activeTool is 'image'
      // This preserves other tools like 'web' and 'code'
      if (activeTool === "image") {
        setActiveTool(null);
      }
    }
  };

  // Helper function to check if a message is from an image or multimodal model
  const isImageOrMultimodalResponse = useCallback(
    (message) => {
      if (!message.modelTitle || !models.length) return false;

      const model = models.find((m) => m.title === message.modelTitle);
      return (
        model &&
        (model.modelType === "image" || model.modelType === "multimodal")
      );
    },
    [models]
  );

  // Handle opening image modal for larger preview
  const handleImageClick = useCallback((imageSrc, imageAlt, messageId) => {
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setModalMessageId(messageId);
    setShowImageModal(true);
  }, []);

  // Handle closing image modal
  const handleCloseImageModal = useCallback(() => {
    setShowImageModal(false);
    setModalImageSrc("");
    setModalImageAlt("");
    setModalMessageId("");
  }, []);

  // Handle persona selection
  const handlePersonaSelect = useCallback((persona) => {
    setSelectedPersona(persona);
    logger.debug("Selected persona", { persona });
    // TODO: Implement persona-specific logic
  }, []);

  // Calculate smart positioning for regenerate dropdown
  const calculateDropdownPosition = useCallback((buttonElement) => {
    if (!buttonElement) return "bottom";

    const buttonRect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 400; // Approximate height of dropdown

    // Check if there's enough space below
    const spaceBelow = viewportHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;

    // If there's not enough space below but enough above, position on top
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return "top";
    }

    return "bottom";
  }, []);

  // Handle regenerating a specific message with a different model
  const handleRegenerateWithModel = useCallback(
    async (modelId, targetMessageId) => {
      logger.debug("handleRegenerateWithModel called", { modelId, targetMessageId });
      try {
        // Find the target message (bot message)
        const targetMessage = messages.find(
          (msg) => msg.id === targetMessageId
        );
        logger.debug("Target message found", { targetMessage: !!targetMessage });
        if (!targetMessage) {
          logger.error("handleRegenerateWithModel: Target message not found");
          toast.error("Message not found");
          return;
        }
        // Find the user message immediately before the selected bot message
        const messageIndex = messages.findIndex(
          (msg) => msg.id === targetMessageId
        );
        logger.debug("Message index", { messageIndex });
        let lastUserMessageId = null;
        for (let i = messageIndex - 1; i >= 0; i--) {
          if (messages[i].type === "user") {
            lastUserMessageId = messages[i].mongoId;
            logger.debug("Found user message with mongoId", { lastUserMessageId });
            break;
          }
        }
        if (!lastUserMessageId) {
          logger.error("Cannot find user message mongoId", {
            messagesAroundTarget: messages.slice(Math.max(0, messageIndex - 3), messageIndex + 1).length
          });
          toast.error("Cannot regenerate: messageId is missing.");
          return;
        }
        logger.debug("handleRegenerateWithModel: Found user message mongoId", { lastUserMessageId });
        // Get user data
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = userData?._id;
        // Close the dropdown
        setShowRegenerateDropdown(null);
        setIsLoadingHistory(true);

        // Prune messages after the selected user message (including the bot message)
        const prunedMessages = messages.slice(0, messageIndex);
        setMessages([
          ...prunedMessages,
          {
            id: `local-ai-${Date.now()}`,
            type: "assistant",
            content: "",
            reasoning: "", // initialize streamed reasoning accumulator
            reasoningStreaming: false,
            isLoading: true,
            timestamp: new Date().toISOString(),
          },
        ]);
        // Streaming logic for regenerate
        logger.debug("Calling regenerateMessageStream", {
          userId,
          threadId: currentThreadId,
          messageId: lastUserMessageId,
          modelId,
        });

        try {
          await regenerateMessageStream({
            userId,
            threadId: currentThreadId,
            messageId: lastUserMessageId,
            modelId,
            onMessage: (chunk, isDone, data) => {
              if (isDone === true) {
                setIsLoadingHistory(false);
                // Mark reasoning as finished for the placeholder
                setMessages((prev) => {
                  return prev.map((msg, idx) =>
                    idx === prev.length - 1 && msg.type === "assistant"
                      ? { ...msg, isLoading: false, reasoningStreaming: false }
                      : msg
                  );
                });
                // Clear cache for current thread to ensure fresh data
                messagesCache.current.delete(currentThreadId);
                // Fetch latest chat history to sync UI with longer delay to ensure backend saves
                setTimeout(() => {
                  fetchAndSyncHistory(
                    userId,
                    userData?.organization?._id || userData?.organizationId,
                    currentThreadId
                  );
                }, 1000); // Increased delay to 1 second
                return;
              }

              // Handle structured SSE events like reasoning tokens
              if (data && data.type === "reasoning") {
                setMessages((prev) => {
                  return prev.map((msg, idx) =>
                    idx === prev.length - 1 && msg.type === "assistant"
                      ? {
                          ...msg,
                          reasoning: (msg.reasoning || "") + (data.content || ""),
                          reasoningStreaming: true,
                          isLoading: true,
                        }
                      : msg
                  );
                });
                return;
              }

              if (chunk) {
                console.log('🔄 REGENERATE CHUNK:', chunk);
                setMessages((prev) => {
                  const lastAssistantIdx = prev.length - 1;
                  const lastAssistantMsg = prev[lastAssistantIdx];
                  
                  if (lastAssistantMsg && lastAssistantMsg.type === "assistant") {
                    // Add to word queue
                    addToWordQueue(lastAssistantMsg.id, chunk);
                    
                    return prev.map((msg, idx) =>
                      idx === lastAssistantIdx
                        ? {
                            ...msg,
                            isLoading: true,
                            reasoningStreaming: false,
                          }
                        : msg
                    );
                  }
                  return prev;
                });
              }
            },
          });
        } catch (streamError) {
          Sentry.captureException(streamError);
          logger.error("Streaming error in regenerate", { error: streamError.message });
          setMessages((prev) => {
            const filtered = prev.filter((msg) => !msg.isLoading);
            return [
              ...filtered,
              {
                id: `error-${Date.now()}`,
                type: "error",
                content:
                  streamError?.response?.data?.error ||
                  streamError?.message ||
                  "Failed to regenerate response.",
                timestamp: new Date().toISOString(),
              },
            ];
          });
          setIsLoadingHistory(false);
        }
      } catch (error) {
        Sentry.captureException(error);
        logger.error("Error regenerating message", { error: error.message });
        toast.error(
          error.response?.data?.error || "Failed to regenerate response."
        );
        setIsLoadingHistory(false);
      }
    },
    [messages, currentThreadId, fetchAndSyncHistory, messagesCache]
  );

  // Initialize models on component mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const availableModels = await getAvailableModelsForDropdown();

        setModels(availableModels);
      } catch (error) {
        Sentry.captureException(error);
        logger.error("Error loading models", { error: error.message });
        toast.error(chatError || "Failed to load models");
      }
    };

    loadModels();
  }, [
    selectedModel,
    getAvailableModelsForDropdown,
    getDefaultModelId,
    chatError,
  ]);

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handlePromptChange = React.useCallback((e) => {
    setPrompt(e.target.value);
  }, []);

  // Create a new chat thread
  const createNewThread = useCallback(async () => {
    // Clear the current thread, messages, and selected persona, then navigate to /chat
    setCurrentThreadId(null);
    setMessages([]);
    setSelectedPersona(null); // Clear selected persona when starting new chat
    navigate("/chat", { replace: true });
    return null;
  }, [navigate]);

  const [file, setFile] = useState(null);
  const [activeTool, setActiveTool] = useState(null); // 'code' | 'web' | null

  // Effect to handle tool selection - automatically open right panel
  useEffect(() => {
    if (activeTool === "youtube") {
      setActiveRightPanel("youtube");
      setRightPanelExpanded(true);
    } else if (activeTool === "research") {
      setActiveRightPanel("research");
      setRightPanelExpanded(true);
    } else if (
      (activeRightPanel === "youtube" && activeTool !== "youtube") ||
      (activeRightPanel === "research" && activeTool !== "research")
    ) {
      // Close right panel when tool is deselected
      setRightPanelExpanded(false);
      setActiveRightPanel("models"); // Reset to default panel
    }
  }, [activeTool, activeRightPanel]);

  // Update handleSubmit to accept promptOverride
  const handleSubmit = useCallback(
    async (e, fileToSend, _activeTool, promptOverride, tool) => {
      e.preventDefault();
      const originalPrompt = (
        promptOverride !== undefined ? promptOverride : prompt
      ).trim();
      if (!originalPrompt || isChatLoading) return;

      // No need to prepare or prepend system prompt; backend handles it
      const backendPrompt = originalPrompt;

      // If no thread is selected, create a new thread first
      let threadIdToUse = currentThreadId;
      if (!threadIdToUse) {
        // Actually create a new thread in the backend and get its id
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        const userId = userData?._id; // Will be undefined for anonymous users
        const organizationId = getOrganizationId(userData);

        setIsLoadingHistory(true);
        try {
          // 7. Create Thread API: called when starting a new chat thread (user sends first message)
          // For anonymous users, userId will be undefined - backend will generate one from session
          logger.info('[Chat] Creating new thread', {
            hasUserId: !!userId,
            isAnonymous: !userId,
            organizationId: organizationId || null
          });
          
          const response = await createThread(userId, organizationId);
          if (response?.data?.threadId) {
            threadIdToUse = response.data.threadId;
            setCurrentThreadId(threadIdToUse);
            const newThread = {
              threadId: threadIdToUse,
              name: "Unnamed Thread",
              updatedAt: new Date().toISOString(),
            };
            setChatThreads((prevThreads) => [newThread, ...prevThreads]);
            navigate(`/chat/${threadIdToUse}`, { replace: true });
            // Update page title for new thread
            updatePageTitle("Unnamed Thread");
            logger.info('[Chat] Thread created successfully', { threadIdToUse });
          } else {
            logger.error('[Chat] Failed to create thread - no threadId in response');
            toast.error("Failed to create a new thread.");
            setIsLoadingHistory(false);
            return;
          }
        } catch (err) {
          Sentry.captureException(err);
          logger.error('[Chat] Error creating thread', {
            error: err.message,
            status: err.response?.status
          });
          const errorMessage =
            err?.response?.data?.error ||
            err?.message ||
            "Failed to create a new thread.";
          setErrorModalMessage(errorMessage);
          setShowErrorModal(true);
          setIsLoadingHistory(false);
          return;
        } finally {
          setIsLoadingHistory(false);
        }
      }

      setPrompt(""); // Clear the input field immediately
      setFile(null); // Clear the file after sending

      // IMAGE GENERATION HANDLING
      // Check if activeTool is 'image' OR if selected model is an image model
      const selectedModelData = models.find(
        (model) => model._id === selectedModel
      );
      const shouldGenerateImage =
        _activeTool === "image" ||
        (selectedModelData && selectedModelData.modelType === "image");

      // Frontend debug logging for image prompt enhancement
      if (shouldGenerateImage) {
        const willEnhanceOnBackend =
          selectedModelData &&
          selectedModelData.modelType === "image" &&
          selectedModelData.enhancePrompt;

        logger.info("Image Generation Request", {
          selectedModelId: selectedModel,
          selectedModelName: selectedModelData?.name,
          modelType: selectedModelData?.modelType,
          enhancePrompt: !!selectedModelData?.enhancePrompt,
          willEnhanceOnBackend
        });
      }

      if (shouldGenerateImage) {
        logger.info('[Frontend] IMAGE GENERATION REQUEST STARTED', {
          modelId: selectedModel,
          modelName: selectedModelData?.name,
          promptLength: originalPrompt.length,
          hasInputImage: !!fileToSend,
          threadId: threadIdToUse,
          timestamp: new Date().toISOString()
        });

        // Add user message
        let userMessage = {
          id: `local-user-${Date.now()}`,
          mongoId: null,
          type: "user",
          content: originalPrompt,
          timestamp: new Date().toISOString(),
        };

        if (fileToSend) {
          logger.debug('[Frontend] User uploaded input image for editing');
          
          // Handle case where fileToSend might be an array (multiple files)
          const filesToProcess = Array.isArray(fileToSend) ? fileToSend : [fileToSend];
          
          logger.debug('[Frontend] Processing files for image generation', {
            fileCount: filesToProcess.length,
            files: filesToProcess.map(f => ({ name: f?.name, type: f?.type, isFile: f instanceof File }))
          });
          
          // Process all files and create attachments
          const attachments = [];
          for (const fileToProcess of filesToProcess) {
            // Validate that fileToProcess is a valid File or Blob
            if (!fileToProcess || !(fileToProcess instanceof File || fileToProcess instanceof Blob)) {
              logger.error('[Frontend] Invalid file object for createObjectURL', {
                fileToProcess,
                type: typeof fileToProcess,
                isFile: fileToProcess instanceof File,
                isBlob: fileToProcess instanceof Blob,
                constructor: fileToProcess?.constructor?.name
              });
              toast.error(`Invalid file object: ${fileToProcess?.name || 'unknown'}. Please try selecting the file again.`);
              continue; // Skip this file but continue with others
            }
            
            try {
              const localImageUrl = URL.createObjectURL(fileToProcess);
              logger.debug('[Frontend] Successfully created object URL for image', {
                fileName: fileToProcess.name,
                fileType: fileToProcess.type,
                fileSize: fileToProcess.size
              });
              attachments.push({
                type: "image",
                url: localImageUrl,
                name: fileToProcess.name,
                mimeType: fileToProcess.type,
              });
            } catch (error) {
              logger.error('[Frontend] Failed to create object URL', {
                error: error.message,
                errorName: error.name,
                fileName: fileToProcess?.name,
                fileType: fileToProcess?.type,
                fileSize: fileToProcess?.size,
                isFile: fileToProcess instanceof File,
                isBlob: fileToProcess instanceof Blob
              });
              toast.error(`Failed to process image ${fileToProcess?.name || 'unknown'}: ${error.message}`);
              continue; // Skip this file but continue with others
            }
          }
          
          if (attachments.length === 0) {
            toast.error("No valid files could be processed. Please try selecting files again.");
            return;
          }
          
          // For image generation, we typically only use the first file, but show all in preview
          userMessage.attachments = attachments;
          
          // Log warning if multiple files were provided for image generation (only first will be used by backend)
          if (attachments.length > 1) {
            logger.warn('[Frontend] Multiple files provided for image generation - only first file will be used by backend', {
              fileCount: attachments.length
            });
          }
        }

        setMessages((prev) => {
          let filtered = prev.filter((m) => m.type !== "error");
          filtered = filtered.filter((m, i, arr) => {
            if (m.type !== "user") return true;
            return arr[i + 1] && arr[i + 1].type === "assistant";
          });
          return [...filtered, userMessage];
        });
        
        logger.debug('[Frontend] User message added to chat');
        
        // Add loading assistant message with start time for timing tracking
        const imageGenerationStartTime = Date.now();
        setMessages((prev) => [
          ...prev,
          {
            id: `local-ai-image-${Date.now()}`,
            type: "assistant",
            content: "",
            isLoading: true,
            timestamp: new Date().toISOString(),
            isImage: true,
            imageUrl: null,
            imageGenerationStartTime, // Track start time for response timing
          },
        ]);
        logger.debug('[Frontend] Loading assistant message added, waiting for response...');
        
        // One-time push to bottom on send for better UX
        setTimeout(() => smoothScrollToBottom(800), 0);
        try {
          const userData = JSON.parse(localStorage.getItem("user") || "{}");
          const organizationId = getOrganizationId(userData);
          const userId = userData?._id;
          
          logger.debug('[Frontend] Sending request to backend...');
          // For image generation, use first file if multiple files are provided
          // Use fileToSend if available, otherwise fall back to file state
          let fileToUpload = fileToSend || file;
          
          // If fileToUpload is an array, use the first file for image editing
          if (Array.isArray(fileToUpload)) {
            if (fileToUpload.length > 0) {
              fileToUpload = fileToUpload[0];
              logger.debug('[Frontend] Multiple files provided for image generation, using first file', {
                totalFiles: fileToSend.length,
                usingFile: fileToUpload.name
              });
            } else {
              fileToUpload = null;
            }
          }
          
          logger.debug('[Frontend] File check before generateImage', {
            hasFile: !!fileToUpload,
            fileName: fileToUpload?.name,
            isArray: Array.isArray(fileToSend)
          });
          
          const response = await generateImage({
            message: backendPrompt,
            userId,
            organizationId,
            threadId: threadIdToUse,
            modelId: selectedModel,
          }, fileToUpload); // Pass input image file for image editing (single file)
          
          logger.debug('[Frontend] Received response from backend');
          const imageUrl = response?.data?.images?.[0]?.url;
          logger.debug('[Frontend] Generated image URL received', { hasUrl: !!imageUrl });
          
          // Frontend logging for prompt enhancement (if enhanced)
          const promptInfo = response?.data?.promptInfo;
          if (promptInfo && promptInfo.wasEnhanced) {
            logger.info("Image Prompt Enhancement", {
              model: selectedModelData?.name,
              detectedLanguage: promptInfo.detectedLanguage,
              wasEnhanced: true
            });
          } else if (promptInfo && !promptInfo.wasEnhanced) {
            logger.debug("Image Generation (No Enhancement)", {
              model: selectedModelData?.name
            });
          }
          if (imageUrl) {
            logger.debug('[Frontend] Image URL received, updating UI...');
            setMessages((prev) => {
              return prev.map((msg, idx) =>
                idx === prev.length - 1 && msg.type === "assistant"
                  ? {
                      ...msg,
                      content: fileToSend
                        ? "Here is your edited image."
                        : "Here is your generated image.",
                      isLoading: false,
                      isImage: true,
                      imageUrl,
                    }
                  : msg
              );
            });
            logger.debug('[Frontend] UI updated with generated image');
            
            // Always sync history after image generation
            logger.debug('[Frontend] Syncing chat history...');
            setTimeout(() => {
              fetchAndSyncHistory(userId, organizationId, threadIdToUse);
              // Trigger usage UI refresh (images count) after DB save settles
              window.dispatchEvent(new Event("usage:refresh"));
              logger.debug('[Frontend] Chat history synced');
            }, 1000);
            
            logger.info('[Frontend] IMAGE GENERATION COMPLETED SUCCESSFULLY');
          } else {
            logger.error('[Frontend] No image URL in response');
            throw new Error('No image URL received from backend');
          }
        } catch (error) {
          Sentry.captureException(error);
          logger.error('[Frontend] IMAGE GENERATION FAILED', {
            message: error.message,
            status: error.response?.status
          });
          // Build a user-friendly error message without changing existing bubble logic
          const data = error?.response?.data || {};
          const status = error?.response?.status;
          let messageText =
            (typeof data.error === "string" && data.error) ||
            (typeof error?.message === "string" && error.message) ||
            "Image generation failed.";

          // If rate limited / limit reached, append helpful details
          if (
            status === 429 ||
            data?.limit !== undefined ||
            data?.used !== undefined
          ) {
            const parts = [];
            if (
              typeof data.used === "number" &&
              typeof data.limit === "number"
            ) {
              parts.push(`${data.used}/${data.limit} used`);
            }
            if (data.resetTime) {
              parts.push(`resets at ${data.resetTime}`);
            }
            if (parts.length) {
              messageText = `${messageText} (${parts.join(", ")})`;
            }
          }

          setMessages((prev) => [
            ...prev.slice(0, -1),
            {
              id: `error-${Date.now()}`,
              type: "error",
              content: messageText,
              timestamp: new Date().toISOString(),
            },
          ]);
          // Also refresh usage, as limits may have been reached
          window.dispatchEvent(new Event("usage:refresh"));
        }
        return;
      }

      try {
        // Get user data from localStorage
        const userData = JSON.parse(localStorage.getItem("user") || "{}");
        const organizationId = getOrganizationId(userData);
        const userId = userData?._id;

        // Remove all error bubbles and orphaned user messages, then add the new user message
        let userMessage = {
          id: `local-user-${Date.now()}`,
          mongoId: null, // Not saved yet
          type: "user",
          content: originalPrompt,
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => {
          // Remove all error bubbles
          let filtered = prev.filter((m) => m.type !== "error");
          // Remove any user message not followed by an assistant message
          filtered = filtered.filter((m, i, arr) => {
            if (m.type !== "user") return true;
            // If the next message is not an assistant, remove this user message
            return arr[i + 1] && arr[i + 1].type === "assistant";
          });
          return [...filtered, userMessage];
        });

        let localImageUrl = null;
        if (fileToSend) {
          // Handle case where fileToSend might be an array (multiple files)
          const filesToProcess = Array.isArray(fileToSend) ? fileToSend : [fileToSend];
          
          logger.debug('[Frontend] Processing files for chat message', {
            fileCount: filesToProcess.length,
            files: filesToProcess.map(f => ({ name: f?.name, type: f?.type, isFile: f instanceof File }))
          });
          
          // Process all files and create attachments
          const attachments = [];
          for (const fileToProcess of filesToProcess) {
            // Validate that fileToProcess is a valid File or Blob
            if (!fileToProcess || !(fileToProcess instanceof File || fileToProcess instanceof Blob)) {
              logger.error('[Frontend] Invalid file object for createObjectURL', {
                fileToProcess,
                type: typeof fileToProcess,
                isFile: fileToProcess instanceof File,
                isBlob: fileToProcess instanceof Blob,
                constructor: fileToProcess?.constructor?.name
              });
              toast.error(`Invalid file object: ${fileToProcess?.name || 'unknown'}. Please try selecting the file again.`);
              continue; // Skip this file but continue with others
            }
            
            try {
              const fileUrl = URL.createObjectURL(fileToProcess);
              logger.debug('[Frontend] Successfully created object URL', {
                fileName: fileToProcess.name,
                fileType: fileToProcess.type,
                fileSize: fileToProcess.size,
                isImage: fileToProcess.type?.startsWith("image")
              });
              
              // Store first file's URL for cleanup (if needed)
              if (!localImageUrl) {
                localImageUrl = fileUrl;
              }
              
              attachments.push({
                type: fileToProcess.type.startsWith("image") ? "image" : "file",
                url: fileUrl,
                name: fileToProcess.name,
                mimeType: fileToProcess.type,
                size: fileToProcess.size,
              });
            } catch (error) {
              logger.error('[Frontend] Failed to create object URL', {
                error: error.message,
                errorName: error.name,
                fileName: fileToProcess?.name,
                fileType: fileToProcess?.type,
                fileSize: fileToProcess?.size,
                isFile: fileToProcess instanceof File,
                isBlob: fileToProcess instanceof Blob
              });
              toast.error(`Failed to process file ${fileToProcess?.name || 'unknown'}: ${error.message}`);
              continue; // Skip this file but continue with others
            }
          }
          
          if (attachments.length === 0) {
            toast.error("No valid files could be processed. Please try selecting files again.");
            return;
          }
          
          userMessage.attachments = attachments;
          
          logger.debug('[Frontend] Created attachments for user message', {
            attachmentCount: attachments.length
          });
        }

        // In handleSubmit and anywhere a message is sent, determine which model to use
        // If selectedModel is empty string (""), it means "Default/Auto" was selected - pass empty string to backend
        // If selectedModel is null/undefined, fall back to first model (for backwards compatibility)
        // Otherwise, use the selected model
        const modelIdToUse = selectedModel === "" 
          ? "" // Empty string triggers backend auto model selection
          : selectedModel || 
            (models && models[0]?.value) || 
            (models && models[0]?._id) || 
            (models && models[0]?.name);

        // Construct YouTube settings object if YouTube tool is active
        let youtubeSettingsObj = null;
        if (_activeTool === "youtube") {
          youtubeSettingsObj = {
            youtubeUrl: backendPrompt, // The prompt should contain the YouTube URL
            action: youtubeActiveTab, // 'summarize' or 'transcribe'
            tone: youtubeTone, // 'concise', 'friendly', 'technical'
            summaryType: youtubeSummaryType, // 'short', 'medium', 'long', 'bullets', 'chapters' - format type
            length:
              youtubeSummaryType === "bullets" ||
              youtubeSummaryType === "chapters"
                ? "medium"
                : youtubeSummaryType, // 'short', 'medium', 'long' - length (default to medium for bullets/chapters)
            language: youtubeLanguage, // 'English', 'Spanish', etc.
          };
        }

        // Construct Research settings object if Research tool is active
        let researchSettingsObj = null;
        if (_activeTool === "research") {
          // Map frontend research modes to backend expected values
          const researchModeMap = {
            simple: "simple_question",
            research: "research_prompt",
            deep: "deep_dive",
            compare: "compare_mode",
          };

          researchSettingsObj = {
            research_mode: researchModeMap[researchMode] || "simple_question",
            citation_style: citationStyle,
            web_sources: includeWebSources,
            document_sources: includeDocSources,
            ai_reasoning: includeAIReasoning,
          };

          logger.debug("Research Settings", { researchSettingsObj });
        }

        // Construct model settings object
        const modelSettingsObj = {
          temperature,
          top_p: topP,
          max_tokens: outputLength,
          system_instructions: instructions || undefined,
        };

        // Streaming logic
        if (!fileToSend) {
          // Check if the selected model is nano banana (Gemini 2.5 Flash Image - production or preview)
          const selectedModelData = models.find((m) => m._id === modelIdToUse);
          const isNanoBananaModel =
            selectedModelData?.name === "google/gemini-2.5-flash-image" ||
            selectedModelData?.name === "google/gemini-2.5-flash-image-preview";

          // Reset fallback state when starting a new message (will be set to true if fallback occurs)
          setIsUsingFallbackModel(false);

          // Append a new assistant message for streaming (with isLoading)
          setMessages((prev) => [
            ...prev,
            {
              id: `local-ai-${Date.now()}`,
              type: "assistant",
              content: "",
              isLoading: true,
              timestamp: new Date().toISOString(),
              reasoning: "",
              reasoningStreaming: false,
              // Add image generation fields if nano banana is selected
              ...(isNanoBananaModel && {
                isImage: true,
                imageUrl: null,
                imageGenerationStartTime: Date.now(),
              }),
            },
          ]);
          // One-time push to bottom on send
          setTimeout(() => smoothScrollToBottom(800), 0);

          try {
            // Normalize fileToSend to array format for backend (useChat hook handles both single and array)
            const filesToSend = fileToSend ? (Array.isArray(fileToSend) ? fileToSend : [fileToSend]) : null;
            
            await sendChatMessage(
              modelIdToUse,
              [...messagesRef.current, userMessage],
              backendPrompt, // now always just the raw prompt
              userId,
              organizationId,
              threadIdToUse,
              filesToSend, // Pass all files (array or null)
              (chunk, isDone, data) => {
                if (isDone) {
                  // Clean up word ticker and processed content when done
                  setMessages((prev) => {
                    const lastAssistantIdx = prev.length - 1;
                    const lastAssistantMsg = prev[lastAssistantIdx];
                    
                    if (lastAssistantMsg && lastAssistantMsg.type === "assistant") {
                      // Do NOT call cleanupWordTicker here!
                      // We must let the word ticker finish emptying the queue naturally,
                      // so that word-by-word streaming visually completes even if the network transfer is already finished.
                      // The ticker will clean itself up and then trigger history sync.
                    }
                    
                    return prev.map((msg, idx) =>
                      idx === lastAssistantIdx && msg.type === "assistant"
                        ? {
                            ...msg,
                            isLoading: false,
                            reasoningStreaming: false,
                          }
                        : msg
                    );
                  });
                  // fetchAndSyncHistory will be called in finally
                  return;
                }

                // Handle special data types for research tool, reasoning, images, and YouTube
                if (data && typeof data === "object") {
                  if (data.type === "error") {
                    console.error("Streaming error from backend:", data.message);
                    setMessages((prev) => {
                      return prev.map((msg, idx) =>
                        idx === prev.length - 1 && msg.type === "assistant"
                          ? { ...msg, content: `Error: ${data.message || "An unknown error occurred"}` }
                          : msg
                      );
                    });
                    return;
                  }
                  if (data.type === "final_citations" && _activeTool === "research") {
                    // Final citations - update the last message with citations
                    setMessages((prev) => {
                      return prev.map((msg, idx) =>
                        idx === prev.length - 1 && msg.type === "assistant"
                          ? { ...msg, citations: data.citations || [] }
                          : msg
                      );
                    });
                    return;
                  }
                  if (data.type === "youtube_complete" && _activeTool === "youtube") {
                    // YouTube processing complete - handle any special data
                    console.log('🎥 YouTube processing complete:', data);
                    setMessages((prev) => {
                      return prev.map((msg, idx) =>
                        idx === prev.length - 1 && msg.type === "assistant"
                          ? { 
                              ...msg, 
                              citations: data.citations || [],
                              metadata: data.metadata || {}
                            }
                          : msg
                      );
                    });
                    return;
                  }
                  if (data.type === "reasoning") {
                    // Update reasoning content directly without accumulation
                    setMessages((prev) => {
                      return prev.map((msg, idx) =>
                        idx === prev.length - 1 && msg.type === "assistant"
                          ? {
                              ...msg,
                              reasoning: (msg.reasoning || "") + (data.content || ""),
                              reasoningStreaming: true,
                            }
                          : msg
                      );
                    });
                    return;
                  }
                  if (data.type === "image") {
                    // Handle streamed image from nano banana model
                    logger.debug("[Frontend] Received image SSE event, preserving modelTitle if set");
                    setMessages((prev) => {
                      return prev.map((msg, idx) => {
                        if (
                          idx === prev.length - 1 &&
                          msg.type === "assistant"
                        ) {
                          const updated = {
                            ...msg, // Preserve ALL existing properties including modelTitle
                            isImage: true,
                            imageUrl: data.url,
                            isLoading: false,
                            content: data.message || "Generated image",
                          };
                          // Ensure modelTitle is preserved (use existing or "Default" for fallback)
                          if (!updated.modelTitle) {
                            updated.modelTitle = "Default"; // Set to Default if fallback occurred
                            logger.debug("[Frontend] Set modelTitle to 'Default' for image message");
                          } else {
                            logger.debug("[Frontend] Preserved existing modelTitle", { modelTitle: updated.modelTitle });
                          }
                          return updated;
                        }
                        return msg;
                      });
                    });
                    return;
                  }
                  if (data.type === "model_switch") {
                    // Handle model switch event (fallback - show "Default")
                    logger.info("[Frontend] Received model_switch SSE event", {
                      type: data.type,
                      modelTitle: data.modelTitle,
                      isDefault: data.isDefault,
                      isFallback: data.isFallback
                    });
                    
                    // Set fallback state to update header IMMEDIATELY
                    setIsUsingFallbackModel(true);
                    logger.debug("[Frontend] Set isUsingFallbackModel to true - header should show 'Default'");
                    
                    // Update message modelTitle to show "Default" IMMEDIATELY
                    setMessages((prev) => {
                      // Find the last assistant message (could be at any position)
                      let lastAssistantIdx = -1;
                      for (let i = prev.length - 1; i >= 0; i--) {
                        if (prev[i].type === "assistant") {
                          lastAssistantIdx = i;
                          break;
                        }
                      }

                      if (lastAssistantIdx === -1) {
                        logger.warn("[Frontend] No assistant message found, creating placeholder");
                        // Create a placeholder assistant message if none exists
                        return [
                          ...prev,
                          {
                            id: `fallback-${Date.now()}`,
                            type: "assistant",
                            content: "",
                            isLoading: true,
                            modelTitle: "Default",
                            timestamp: new Date().toISOString(),
                          },
                        ];
                      }
                      
                      logger.debug("[Frontend] Found assistant message", { index: lastAssistantIdx });
                      const updated = prev.map((msg, idx) => {
                        if (idx === lastAssistantIdx && msg.type === "assistant") {
                          logger.debug("[Frontend] Updating message modelTitle to 'Default'", { previousTitle: msg.modelTitle });
                          return { 
                            ...msg, 
                            modelTitle: "Default"
                          };
                        }
                        return msg;
                      });
                      
                      return updated;
                    });

                    // Header dropdown will show "Default" because isUsingFallbackModel is set to true
                    // This applies to both image generation fallback and image/PDF input fallback
                    logger.debug("[Frontend] Model switch handled - Header and message should now show 'Default'");
                    return;
                  }
                }

                if (chunk) {
                  let filteredChunk = chunk;
                  
                  // Clean up backend quirks for YouTube recap tool dynamically
                  if (_activeTool === "youtube") {
                    if (filteredChunk.includes("Processing YouTube video...")) {
                      filteredChunk = filteredChunk.replace("Processing YouTube video...", "");
                    }
                    // The backend sometimes uses unicode bullet characters which break Markdown parsing. Convert them.
                    filteredChunk = filteredChunk.replace(/•\s/g, "- ");
                  }

                  if (filteredChunk) {
                    // Word-based streaming like mobile app
                    console.log('🔥 STREAMING CHUNK:', {
                      chunk: filteredChunk,
                      length: filteredChunk.length,
                      tool: _activeTool,
                      timestamp: new Date().toISOString()
                    });
                    
                    // Add chunk to word queue OUTSIDE of setMessages to avoid duplicates
                    const currentMessages = messagesRef.current;
                    const lastAssistantMsg = currentMessages[currentMessages.length - 1];
                    
                    if (lastAssistantMsg && lastAssistantMsg.type === "assistant") {
                      addToWordQueue(lastAssistantMsg.id, filteredChunk);
                    }
                  }
                  
                  // Update loading state only
                  setMessages((prev) => {
                    const lastAssistantIdx = prev.length - 1;
                    return prev.map((msg, idx) =>
                      idx === lastAssistantIdx && msg.type === "assistant"
                        ? {
                            ...msg,
                            isLoading: true,
                            reasoningStreaming: false,
                          }
                        : msg
                    );
                  });
                }
              },
              setMessages,
              _activeTool, // pass tool as argument
              null, // onAbort callback
              selectedPersona?._id, // pass only the persona ID
              _activeTool === "research" ? researchSettingsObj : youtubeSettingsObj, // pass appropriate settings object
              modelSettingsObj // pass model settings object
            );
          } catch (err) {
            // Surface API error in a response bubble without breaking other flows
            Sentry.captureException(err);
            logger.error("Streaming error", { error: err.message });
            const serverMsg =
              err?.response?.data?.error || err?.message || "Request failed";
            setMessages((prev) => {
              // Remove any loading/AI placeholder
              const filtered = prev.filter((msg) => !msg.isLoading);
              return [
                ...filtered,
                {
                  id: `error-${Date.now()}`,
                  type: "error",
                  content: `${serverMsg}`,
                  timestamp: new Date().toISOString(),
                },
              ];
            });
            // Don't call fetchAndSyncHistory on error - preserve the current state
            return;
          }

          // Re-enable fetchAndSyncHistory but only after streaming completes to preserve content
          setTimeout(() => {
            // Wait for word ticker to finish before syncing
            const checkStreamingComplete = () => {
              const lastMsg = messagesRef.current[messagesRef.current.length - 1];
              if (lastMsg && lastMsg.type === "assistant") {
                const hasActiveQueue = wordQueues.current[lastMsg.id] && wordQueues.current[lastMsg.id].length > 0;
                const hasActiveTicker = wordTickers.current[lastMsg.id];
                
                if (!hasActiveQueue && !hasActiveTicker) {
                  // Streaming is complete, now sync to persist the message
                  console.log('🔄 Streaming complete, syncing with backend...');
                  fetchAndSyncHistory(
                    userId,
                    organizationId,
                    threadIdToUse,
                    localImageUrl,
                    true // Preserve streamed content
                  );
                  // Dispatch a global usage refresh event so UI updates without page reload
                  window.dispatchEvent(new Event("usage:refresh"));
                } else {
                  // Still streaming, check again later
                  console.log('⏳ Still streaming, checking again...', { hasActiveQueue, hasActiveTicker });
                  setTimeout(checkStreamingComplete, 500);
                }
              }
            };
            
            checkStreamingComplete();
          }, 2000); // Initial delay to let streaming get started
          return;
        }
        // Fallback to non-streaming for file uploads
        // Reset fallback state when starting a new message with file (will be set to true if fallback occurs)
        setIsUsingFallbackModel(false);

        setMessages((prev) => [
          ...prev,
          {
            id: `local-ai-${Date.now()}`,
            type: "assistant",
            content: "",
            isLoading: true,
            timestamp: new Date().toISOString(),
          },
        ]);
        // One-time push to bottom on send
        setTimeout(() => smoothScrollToBottom(800), 0);

        await sendChatMessage(
          modelIdToUse,
          [...messagesRef.current, userMessage],
          backendPrompt, // now always just the raw prompt
          userId,
          organizationId,
          threadIdToUse,
          fileToSend,
          (chunk, isDone, data) => {
            if (isDone) {
              // Mark as finished
              setMessages((prev) => {
                return prev.map((msg, idx) =>
                  idx === prev.length - 1 && msg.type === "assistant"
                    ? { ...msg, isLoading: false }
                    : msg
                );
              });
              return;
            }

            // Handle special data types for model_switch events
            if (data && typeof data === "object") {
              logger.debug("[Frontend] Received data object in file upload callback", { dataType: data.type });
              if (data.type === "model_switch") {
                // Handle model switch event (fallback - show "Default")
                logger.info("[Frontend] Received model_switch SSE event (file upload)", {
                  type: data.type,
                  modelTitle: data.modelTitle,
                  isDefault: data.isDefault,
                  isFallback: data.isFallback
                });
                
                // Set fallback state to update header IMMEDIATELY
                setIsUsingFallbackModel(true);
                logger.debug("[Frontend] Set isUsingFallbackModel to true (file upload) - header should show 'Default'");
                
                // Update message modelTitle to show "Default" IMMEDIATELY
                setMessages((prev) => {
                  let lastAssistantIdx = -1;
                  for (let i = prev.length - 1; i >= 0; i--) {
                    if (prev[i].type === "assistant") {
                      lastAssistantIdx = i;
                      break;
                    }
                  }

                  if (lastAssistantIdx === -1) {
                    logger.warn("[Frontend] No assistant message found (file upload), creating placeholder");
                    return [
                      ...prev,
                      {
                        id: `fallback-${Date.now()}`,
                        type: "assistant",
                        content: "",
                        isLoading: true,
                        modelTitle: "Default",
                        timestamp: new Date().toISOString(),
                      },
                    ];
                  }
                  
                  logger.debug("[Frontend] Found assistant message (file upload)", { index: lastAssistantIdx });
                  const updated = prev.map((msg, idx) => {
                    if (idx === lastAssistantIdx && msg.type === "assistant") {
                      logger.debug("[Frontend] Updating message modelTitle to 'Default' (file upload)", { previousTitle: msg.modelTitle });
                      return { 
                        ...msg, 
                        modelTitle: "Default"
                      };
                    }
                    return msg;
                  });
                  
                  return updated;
                });

                // Header dropdown will show "Default" because isUsingFallbackModel is set to true
                logger.debug("[Frontend] Model switch handled (file upload) - Header and message should now show 'Default'");
                return;
              }
            }

            if (chunk) {
              // Word-based streaming for file uploads
              console.log('📁 FILE UPLOAD CHUNK:', chunk);
              setMessages((prev) => {
                const lastAssistantIdx = prev.length - 1;
                const lastAssistantMsg = prev[lastAssistantIdx];
                
                if (lastAssistantMsg && lastAssistantMsg.type === "assistant") {
                  // Add to word queue
                  addToWordQueue(lastAssistantMsg.id, chunk);
                  
                  return prev.map((msg, idx) =>
                    idx === lastAssistantIdx
                      ? { ...msg, isLoading: true }
                      : msg
                  );
                }
                return prev;
              });
            }
          },
          _activeTool, // pass tool as argument
          null, // onAbort callback
          selectedPersona?._id, // pass only the persona ID
          _activeTool === "research" ? researchSettingsObj : youtubeSettingsObj, // pass appropriate settings object
          modelSettingsObj // pass model settings object
        );
        // 3. History API: called after each LLM response (after streaming or file upload)
        // Re-enable fetchAndSyncHistory for file uploads but wait for streaming to complete
        setTimeout(() => {
          const checkStreamingComplete = () => {
            const lastMsg = messagesRef.current[messagesRef.current.length - 1];
            if (lastMsg && lastMsg.type === "assistant") {
              const hasActiveQueue = wordQueues.current[lastMsg.id] && wordQueues.current[lastMsg.id].length > 0;
              const hasActiveTicker = wordTickers.current[lastMsg.id];
              
              if (!hasActiveQueue && !hasActiveTicker) {
                // Streaming is complete, now sync
                console.log('🔄 File upload streaming complete, syncing with backend...');
                fetchAndSyncHistory(
                  userId,
                  organizationId,
                  threadIdToUse,
                  localImageUrl,
                  true // Preserve streamed content
                );
                // Dispatch a global usage refresh event so UI updates without page reload
                window.dispatchEvent(new Event("usage:refresh"));
              } else {
                // Still streaming, check again later
                setTimeout(checkStreamingComplete, 500);
              }
            }
          };
          
          checkStreamingComplete();
        }, 4000);
      } catch (error) {
        // Reset streaming flag on error

        // Get error message from response
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to send message. Please try again.";

        logger.debug("Final error message", { errorMessage });

        // Remove loading/AI placeholder and add error bubble
        setMessages((prev) => {
          // Remove any loading/AI placeholder
          const filtered = prev.filter((msg) => !msg.isLoading);
          return [
            ...filtered,
            {
              id: `error-${Date.now()}`,
              type: "error",
              content: errorMessage,
              timestamp: new Date().toISOString(),
            },
          ];
        });
      }
    },
    [
      prompt,
      selectedModel,
      sendChatMessage,
      isChatLoading,
      currentThreadId,
      navigate,
      fetchAndSyncHistory,
      models,
    ]
  );

  // Custom stop handler: aborts, then fetches latest history
  const handleStop = useCallback(() => {
    stopGenerating();
    // Wait a short moment for backend to save partial message
    setTimeout(() => {
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = userData?._id;
      const organizationId = getOrganizationId(userData);
      // 6. History API: called after aborting/stop generation to fetch the latest partial message
      fetchAndSyncHistory(userId, organizationId, currentThreadId);
    }, 600);
  }, [stopGenerating, fetchAndSyncHistory, currentThreadId]);

  // When chatThreads changes, if the currentThreadId is gone, clear it
  useEffect(() => {
    // Only redirect to /chat if there is no threadId in the URL and no currentThreadId
    if (
      currentThreadId &&
      !chatThreads.some((t) => t.threadId === currentThreadId)
    ) {
      setCurrentThreadId(null);
      setMessages([]);
      if (!threadId) {
        navigate("/chat", { replace: true });
      }
    }
  }, [chatThreads, currentThreadId, navigate, threadId]);

  // Callback for when the current thread is deleted
  const handleCurrentThreadDeleted = useCallback(() => {
    setCurrentThreadId(null);
    setMessages([]);
    navigate("/chat", { replace: true });
  }, [navigate]);

  // Restore refreshThreadList function
  const refreshThreadList = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      const userId = userData?._id;
      const organizationId = getOrganizationId(userData);

      if (!userId) {
        logger.error("User ID not found");
        return;
      }

      // 5. Threads API: called to refresh the thread list (manual refresh or after thread changes)
      const response = await getThreads(userId, organizationId);
      if (Array.isArray(response?.data)) {
        setChatThreads(response.data);
      } else if (response?.data?.threads) {
        setChatThreads(response.data.threads);
      } else {
        setChatThreads([]);
      }
    } catch (error) {
      Sentry.captureException(error);
      logger.error("Error refreshing thread list", { error: error.message });
      toast.error("Failed to refresh thread list");
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // When URL changes, manage tools route and thread selection
  useEffect(() => {
    const isTools = location.pathname === "/chat/tools";
    setShowToolsLanding(isTools);
    if (isTools) {
      updatePageTitle("Tools");
      return;
    }
    if (threadId) {
      // Only load if not already loaded or if currentThreadId is different
      if (threadId !== currentThreadId || messages.length === 0) {
        setCurrentThreadId(threadId);
        // Find the thread in chatThreads
        const existingThread = chatThreads.find((t) => t.threadId === threadId);
        if (existingThread) {
          handleChatSelect(existingThread);
        }
      }
    } else {
      // No thread selected, show general chat title
      updatePageTitle("Chat");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, chatThreads, location.pathname]);

  // Add a ref map for assistant messages
  const assistantRefs = useRef({});

  return (
    <div className="flex h-screen bg-bg-50 text-fg-50 overflow-hidden relative">
      {/* Subscription Status Banner */}
      {showSubscriptionBanner && subscription && (
        <SubscriptionStatusBanner
          subscription={subscription}
          onClose={() => setShowSubscriptionBanner(false)}
        />
      )}

      {/* Mobile overlay backdrop */}
      {(sidebarExpanded || rightPanelExpanded) && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => {
            if (sidebarExpanded) setSidebarExpanded(false);
            if (rightPanelExpanded) setRightPanelExpanded(false);
          }}
        />
      )}

      {/* Sidebar */}
      <ChatSidebar
        sidebarExpanded={sidebarExpanded}
        setSidebarExpanded={setSidebarExpanded}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        chatHistory={
          searchQuery.trim() === ""
            ? chatThreads
            : chatThreads.filter((thread) =>
                (thread.name || "Untitled Chat")
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase())
              )
        }
        setChatThreads={setChatThreads}
        isLoadingHistory={isLoadingHistory}
        refreshThreadList={refreshThreadList}
        handleChatSelect={handleChatSelect}
        createNewThread={createNewThread}
        showProfileDropdown={showProfileDropdown}
        setShowProfileDropdown={setShowProfileDropdown}
        profileDropdownRef={profileDropdownRef}
        handleLogout={handleLogout}
        userData={userData}
        currentThreadId={currentThreadId}
        onCurrentThreadDeleted={handleCurrentThreadDeleted}
        onOpenTools={() => navigate("/chat/tools")}
        showSettingsModal={showSettingsModal}
        setShowSettingsModal={setShowSettingsModal}
        isAnonymous={isAnonymous}
      />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col overflow-hidden bg-bg-70 my-0 md:my-4 rounded-none md:rounded-3xl ${
          !rightPanelExpanded ? "mr-0 md:mr-4" : ""
        }`}
      >
        <ChatHeader
          sidebarExpanded={sidebarExpanded}
          toggleSidebar={toggleSidebar}
          rightPanelExpanded={rightPanelExpanded}
          toggleRightPanel={toggleRightPanel}
          theme={theme}
          toggleTheme={toggleTheme}
          userData={userData}
          hideUsage={showToolsLanding}
          selectedPersona={selectedPersona}
          onPersonaSelect={handlePersonaSelect}
          // Model dropdown props
          models={models}
          selectedModel={selectedModel}
          onModelSelect={handleModelSelect}
          activeTool={activeTool}
          isUsingFallbackModel={isUsingFallbackModel}
        />

        {/* Tools Landing */}
        {showToolsLanding && (
          <ToolsLanding
            onOpenTranscriptify={() => {
              navigate("/transcriptify");
            }}
          />
        )}

        {/* Chat Messages */}
        {!showToolsLanding && (
          <div
            ref={chatContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-3 md:p-6 custom-scrollbar"
          >
            <div className="max-w-3xl mx-auto space-y-4 md:space-y-6">
              {messages.filter(
                (msg) =>
                  !/^<system-prompt>[\s\S]*<\/system-prompt>$/.test(
                    (msg.content || "").trim()
                  )
              ).length === 0 ? (
                <PromptSuggestions
                  onPromptSelect={(prompt) => {
                    setPrompt(prompt);
                    // Optional: Auto-focus the input field
                    setTimeout(() => {
                      document.querySelector("textarea")?.focus();
                    }, 100);
                  }}
                />
              ) : (
                <>
                  {messages
                    .filter(
                      (msg) =>
                        !/^<system-prompt>[\s\S]*<\/system-prompt>$/.test(
                          (msg.content || "").trim()
                        )
                    )
                    .map((message) => {
                      // For assistant messages, create/use a ref
                      let messageBubbleRef = null;
                      if (message.type === "assistant") {
                        if (!assistantRefs.current[message.id]) {
                          assistantRefs.current[message.id] = React.createRef();
                        }
                        messageBubbleRef = assistantRefs.current[message.id];
                      }
                      return (
                        <div
                          key={message.id}
                          className={`flex group ${
                            message.type === "user" || isRTL(message.content)
                              ? "justify-end"
                              : "justify-start"
                          }`}
                        >
                          <div
                            className={`flex items-start gap-2 md:gap-3 ${
                              message.type === "user"
                                ? "max-w-[85%] md:max-w-[80%]"
                                : isRTL(message.content)
                                ? "max-w-full"
                                : "w-full max-w-full"
                            } ${
                              message.type === "user" || isRTL(message.content)
                                ? "flex-row-reverse"
                                : "flex-row"
                            }`}
                          >
                            <div className="relative flex flex-col items-start w-full">
                              {/* File Attachments Preview  */}
                              {message.attachments &&
                                message.attachments.length > 0 &&
                                message.type === "user" && (
                                  <div className="flex justify-end w-full mb-1 mt-6 md:mt-10">
                                    <div className="flex items-center gap-1.5 md:gap-2 bg-bg-40 rounded-lg p-1.5 md:p-2 border border-bd-50">
                                      {message.attachments.map((att, i) => (
                                        <React.Fragment key={i}>
                                          {att.type === "image" ? (
                                            <a
                                              href={att.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-block"
                                            >
                                              <img
                                                src={att.url}
                                                alt={att.name || "attachment"}
                                                className="w-10 h-10 md:w-12 md:h-12 object-cover rounded"
                                              />
                                            </a>
                                          ) : (
                                            <span
                                              className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-lg font-bold text-[10px] md:text-xs text-white mr-1 md:mr-2 select-none border-none"
                                              style={{
                                                background:
                                                  "linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)",
                                                backgroundColor:
                                                  "var(--tw-gradient-stops)",
                                                ...(window.matchMedia &&
                                                window.matchMedia(
                                                  "(prefers-color-scheme: dark)"
                                                ).matches
                                                  ? {
                                                      background:
                                                        "linear-gradient(135deg, #1e40af 0%, #1e293b 100%)",
                                                    }
                                                  : {}),
                                              }}
                                            >
                                              {(att.name &&
                                                att.name
                                                  .split(".")
                                                  .pop()
                                                  .toUpperCase()
                                                  .slice(0, 5)) ||
                                                "FILE"}
                                            </span>
                                          )}
                                          <div className="flex flex-col">
                                            <a
                                              href={att.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="file-name text-fg-80 text-sm font-mono hover:underline"
                                            >
                                              {att.name}
                                            </a>
                                            <span className="file-size text-fg-60 text-xs font-mono">
                                              {att.size
                                                ? (att.size / 1024).toFixed(1)
                                                : ""}{" "}
                                              KB
                                            </span>
                                          </div>
                                        </React.Fragment>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              {/* Message Content */}
                              <div
                                className={`w-full ${
                                  message.type === "user"
                                    ? `flex justify-end ${
                                        !message.attachments?.length
                                          ? "mt-6 md:mt-10"
                                          : ""
                                      }`
                                    : ""
                                }`}
                                ref={messageBubbleRef}
                                dir={isRTL(message.content) ? "rtl" : "ltr"}
                              >
                                <div
                                  className={`rounded-xl md:rounded-2xl px-3 py-2 md:px-4 md:py-2.5 inline-block break-words chat-message text-sm md:text-base ${
                                    message.type === "user"
                                      ? "bg-bg-50 text-fg-50 "
                                      : message.type === "error"
                                      ? "text-xs sm:text-sm bg-red-100 text-red-800 border border-red-400 dark:bg-[#2d1517] dark:text-red-200 dark:border-red-500"
                                      : `${
                                          isRTL(message.content)
                                            ? "text-right"
                                            : "text-left"
                                        } text-fg-50`
                                  }`}
                                  style={{
                                    textAlign: isRTL(message.content)
                                      ? "right"
                                      : "left",
                                    direction: isRTL(message.content)
                                      ? "rtl"
                                      : "ltr",
                                  }}
                                >
                                  {message.type === "error" ? (
                                    <span>{message.content}</span>
                                  ) : message.type === "user" ? (
                                    <div className="whitespace-pre-wrap">
                                      {message.content}
                                    </div>
                                  ) : (
                                    <MessageRenderer
                                      content={message.content}
                                      id={message.id}
                                      attachments={
                                        message.type === "assistant"
                                          ? message.attachments
                                          : []
                                      }
                                      isImage={message.isImage}
                                      imageUrl={message.imageUrl}
                                      isLoading={message.isLoading}
                                      imageGenerationStartTime={
                                        message.imageGenerationStartTime
                                      }
                                      showImageControls={
                                        message.type === "assistant" &&
                                        message.attachments &&
                                        message.attachments.length === 1 &&
                                        message.attachments[0].type === "image"
                                      }
                                      onImageClick={handleImageClick}
                                      citations={message.citations}
                                      reasoning={message.reasoning}
                                      reasoningStreaming={
                                        message.reasoningStreaming
                                      }
                                    />
                                  )}
                                </div>
                              </div>
                              {/* Continue Button for truncated bot message */}
                              {message.type === "assistant" &&
                                message.truncated && (
                                  <button
                                    className="bg-bg-40 mt-1 mb-1 ml-4 px-4 py-2 rounded-xl border border-bd-50 text-sm text-fg-50 hover:bg-bg-30 "
                                    onClick={async () => {
                                      setPrompt("");
                                      // Remove truncated flag immediately
                                      setMessages((prev) => {
                                        if (!prev.length) return prev;
                                        return prev.map((msg, idx) =>
                                          idx === prev.length - 1 &&
                                          msg.type === "assistant"
                                            ? { ...msg, truncated: false }
                                            : msg
                                        );
                                      });
                                      await handleSubmit(
                                        { preventDefault: () => {} },
                                        null,
                                        undefined,
                                        "Continue" // Pass Continue as promptOverride
                                      );
                                    }}
                                  >
                                    Continue
                                  </button>
                                )}
                              {/* Message Actions - Different controls for user vs assistant messages */}
                              {message.content &&
                                message.content.trim() !== "" && (
                                  <div
                                    className={`flex w-full mt-1 gap-1 md:gap-0 ${
                                      message.type === "user"
                                        ? "opacity-100 md:opacity-0 md:group-hover:opacity-100"
                                        : "opacity-100 md:opacity-80 md:hover:opacity-100" // Make assistant controls more visible on mobile
                                    } transition-opacity`}
                                    style={{
                                      justifyContent: isRTL(message.content)
                                        ? "flex-end"
                                        : message.type === "user"
                                        ? "flex-end"
                                        : "flex-start",
                                      maxWidth: "80%",
                                      marginLeft: isRTL(message.content)
                                        ? "auto"
                                        : message.type === "assistant"
                                        ? "0"
                                        : "auto",
                                      marginRight: isRTL(message.content)
                                        ? "0"
                                        : message.type === "assistant"
                                        ? "auto"
                                        : "0",
                                      paddingRight: isRTL(message.content)
                                        ? "0.5rem"
                                        : "0",
                                      paddingLeft:
                                        !isRTL(message.content) &&
                                        message.type === "assistant"
                                          ? "0.5rem"
                                          : "0",
                                    }}
                                  >
                                    {/* User message controls - copy and edit */}
                                    {message.type === "user" && (
                                      <div className="flex items-center rounded-lg p-0.5 pl-4">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyToClipboard(
                                              message.content,
                                              message.id
                                            );
                                          }}
                                          className="p-1.5 text-fg-60 hover:text-fg-50 hover:bg-bg-50 rounded transition-colors"
                                          title="Copy message"
                                        >
                                          {copiedMessageId === message.id ? (
                                            <Check
                                              size={16}
                                              className="text-green-500"
                                            />
                                          ) : (
                                            <Copy size={16} />
                                          )}
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPrompt(message.content);
                                            // Focus the prompt input
                                            setTimeout(() => {
                                              const textarea =
                                                document.querySelector(
                                                  "textarea"
                                                );
                                              if (textarea) {
                                                textarea.focus();
                                                textarea.setSelectionRange(
                                                  textarea.value.length,
                                                  textarea.value.length
                                                );
                                              }
                                            }, 100);
                                          }}
                                          className="p-1.5 text-fg-60 hover:text-fg-50 hover:bg-bg-50 rounded transition-colors"
                                          title="Edit message"
                                        >
                                          <Pencil size={16} />
                                        </button>
                                      </div>
                                    )}

                                    {/* Assistant message controls - full set of controls */}
                                    {message.type === "assistant" &&
                                      !message.isLoading && (
                                        <>
                                          {/* Custom controls for image-only bot messages */}
                                          {message.attachments &&
                                          message.attachments.length === 1 &&
                                          message.attachments[0].type ===
                                            "image" ? (
                                            <div className="flex items-center rounded-lg p-0.5 pl-4">
                                              {/* Like/Dislike */}
                                              <button
                                                onClick={() => {
                                                  setLikedMessages((prev) => ({
                                                    ...prev,
                                                    [message.id]:
                                                      prev[message.id] ===
                                                      "like"
                                                        ? undefined
                                                        : "like",
                                                  }));
                                                }}
                                                className={`p-1.5 rounded focus:outline-none ${
                                                  likedMessages[message.id] ===
                                                  "like"
                                                    ? "text-fg-50"
                                                    : "text-fg-60 hover:text-fg-50 hover:bg-bg-50"
                                                }`}
                                                title="Good response"
                                              >
                                                <ThumbsUp
                                                  size={16}
                                                  fill={
                                                    likedMessages[
                                                      message.id
                                                    ] === "like"
                                                      ? theme === "dark"
                                                        ? "#fff"
                                                        : "#000"
                                                      : "none"
                                                  }
                                                />
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setLikedMessages((prev) => ({
                                                    ...prev,
                                                    [message.id]:
                                                      prev[message.id] ===
                                                      "dislike"
                                                        ? undefined
                                                        : "dislike",
                                                  }));
                                                }}
                                                className={`p-1.5 rounded focus:outline-none ${
                                                  likedMessages[message.id] ===
                                                  "dislike"
                                                    ? "text-fg-50"
                                                    : "text-fg-60 hover:text-fg-50 hover:bg-bg-50"
                                                }`}
                                                title="Bad response"
                                              >
                                                <ThumbsDown
                                                  size={16}
                                                  fill={
                                                    likedMessages[
                                                      message.id
                                                    ] === "dislike"
                                                      ? theme === "dark"
                                                        ? "#fff"
                                                        : "#000"
                                                      : "none"
                                                  }
                                                />
                                              </button>
                                              <button
                                                onClick={async () => {
                                                  const fileName =
                                                    getImageFileName(
                                                      message.attachments[0]
                                                        .url,
                                                      message.id
                                                    );
                                                  const success =
                                                    await downloadImage(
                                                      message.attachments[0]
                                                        .url,
                                                      fileName
                                                    );
                                                  if (!success) {
                                                    toast.error(
                                                      "Failed to download image"
                                                    );
                                                  }
                                                }}
                                                className="p-1.5 text-fg-60 hover:text-fg-50 hover:bg-bg-50 rounded transition-colors"
                                                title="Download image"
                                              >
                                                <svg
                                                  width="16"
                                                  height="16"
                                                  fill="none"
                                                  viewBox="0 0 24 24"
                                                  stroke="currentColor"
                                                >
                                                  <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3"
                                                  />
                                                </svg>
                                              </button>
                                              {/* Model name display for image responses */}
                                              <span
                                                className="ml-2 text-xs text-fg-60 select-none whitespace-nowrap"
                                                style={{
                                                  display: "inline-block",
                                                }}
                                              >
                                                {message.modelTitle ||
                                                  "Image Model"}
                                              </span>
                                            </div>
                                          ) : (
                                            // Full controls for text responses
                                            <div className="flex items-center rounded-lg p-0.5 pl-4">
                                              {/* Copy */}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  copyToClipboard(
                                                    message.content,
                                                    message.id
                                                  );
                                                }}
                                                className="p-2 md:p-1.5 text-fg-60 hover:text-fg-50 hover:bg-bg-50 rounded transition-colors touch-manipulation"
                                                title="Copy message"
                                              >
                                                {copiedMessageId ===
                                                message.id ? (
                                                  <Check
                                                    size={16}
                                                    className="text-green-500"
                                                  />
                                                ) : (
                                                  <Copy size={16} />
                                                )}
                                              </button>
                                              {/* Text-to-Speech - Only for text responses */}
                                              {!isImageOrMultimodalResponse(
                                                message
                                              ) && (
                                                <TextToSpeech
                                                  text={message.content}
                                                  className="touch-manipulation"
                                                />
                                              )}
                                              {/* Download - Hide for image/multimodal responses */}
                                              {!isImageOrMultimodalResponse(
                                                message
                                              ) && (
                                                <DownloadResponse
                                                  content={message.content}
                                                  fileName={`response-${message.id}`}
                                                  messageRef={messageBubbleRef}
                                                />
                                              )}
                                              {/* Show regenerate button on all assistant messages */}
                                              <div
                                                className="relative"
                                                ref={
                                                  showRegenerateDropdown ===
                                                  message.id
                                                  ? regenerateDropdownRef
                                                  : null
                                              }
                                              >
                                                <button
                                                  ref={(el) => {
                                                    if (el) {
                                                      regenerateButtonRefs.current[
                                                        message.id
                                                      ] = el;
                                                    }
                                                  }}
                                                  onClick={(e) => {
                                                    logger.debug("Regenerate button clicked", { messageId: message.id });
                                                    e.stopPropagation();
                                                    if (
                                                      showRegenerateDropdown ===
                                                      message.id
                                                    ) {
                                                      setShowRegenerateDropdown(
                                                        null
                                                      );
                                                    } else {
                                                      // Calculate position before showing dropdown
                                                      const position =
                                                        calculateDropdownPosition(
                                                          e.currentTarget
                                                        );
                                                      setRegenerateDropdownPosition(
                                                        position
                                                      );
                                                      setShowRegenerateDropdown(
                                                        message.id
                                                      );
                                                    }
                                                  }}
                                                  className="p-1.5 text-fg-60 hover:text-fg-50 hover:bg-bg-50 rounded transition-colors"
                                                  title="Regenerate response"
                                                >
                                                  <RefreshCw size={16} />
                                                </button>

                                                {showRegenerateDropdown ===
                                                  message.id && (
                                                  <RegenerateModelsDropdown
                                                    models={models}
                                                    selectedModel={
                                                      selectedModel
                                                    }
                                                    onSelect={(modelId) => {
                                                      logger.debug("onSelect called", { modelId, messageId: message.id });
                                                      handleRegenerateWithModel(
                                                        modelId,
                                                        message.id
                                                      );
                                                    }}
                                                    isLoading={isChatLoading}
                                                    placeholder="Select a model"
                                                    forceOpen={true}
                                                    position={
                                                      regenerateDropdownPosition
                                                    }
                                                    onClose={() =>
                                                      setShowRegenerateDropdown(
                                                        null
                                                      )
                                                    }
                                                    triggerRef={{
                                                      current:
                                                        regenerateButtonRefs
                                                          .current[message.id],
                                                    }}
                                                  />
                                                )}
                                              </div>
                                              <div className="h-4 w-px bg-bd-60/50 mx-0.5"></div>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setLikedMessages((prev) => ({
                                                    ...prev,
                                                    [message.id]:
                                                      prev[message.id] ===
                                                      "like"
                                                        ? undefined
                                                        : "like",
                                                  }));
                                                }}
                                                className={`p-1.5 rounded focus:outline-none ${
                                                  likedMessages[message.id] ===
                                                  "like"
                                                    ? "text-fg-50"
                                                    : "text-fg-60 hover:text-fg-50 hover:bg-bg-50"
                                                }`}
                                                title="Good response"
                                              >
                                                <ThumbsUp
                                                  size={16}
                                                  fill={
                                                    likedMessages[
                                                      message.id
                                                    ] === "like"
                                                      ? theme === "dark"
                                                        ? "#fff"
                                                        : "#000"
                                                      : "none"
                                                  }
                                                />
                                              </button>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setLikedMessages((prev) => ({
                                                    ...prev,
                                                    [message.id]:
                                                      prev[message.id] ===
                                                      "dislike"
                                                        ? undefined
                                                        : "dislike",
                                                  }));
                                                }}
                                                className={`p-1.5 rounded focus:outline-none ${
                                                  likedMessages[message.id] ===
                                                  "dislike"
                                                    ? "text-fg-50"
                                                    : "text-fg-60 hover:text-fg-50 hover:bg-bg-50"
                                                }`}
                                                title="Bad response"
                                              >
                                                <ThumbsDown
                                                  size={16}
                                                  fill={
                                                    likedMessages[
                                                      message.id
                                                    ] === "dislike"
                                                      ? theme === "dark"
                                                        ? "#fff"
                                                        : "#000"
                                                      : "none"
                                                  }
                                                />
                                              </button>
                                              {/* Model name display */}
                                              <span
                                                className="ml-2 text-xs text-fg-60 select-none whitespace-nowrap"
                                                style={{
                                                  display: "inline-block",
                                                }}
                                              >
                                                {message.modelTitle || ""}
                                              </span>
                                            </div>
                                          )}
                                        </>
                                      )}
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  {/* Show loading animation only if the last assistant message is loading and content is empty */}
                  {(() => {
                    const lastMsg = messages[messages.length - 1];
                    if (
                      lastMsg &&
                      lastMsg.type === "assistant" &&
                      lastMsg.isLoading &&
                      !lastMsg.content &&
                      !lastMsg.reasoning &&
                      !lastMsg.reasoningStreaming
                    ) {
                      return (
                        <div className="flex justify-start">
                          <div className="flex items-start gap-3 w-full">
                            <div className="px-4 py-2.5">
                              <MessageLoading />
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </>
              )}
            </div>
          </div>
        )}

        {/* Input Area */}
        {!showToolsLanding && (
          <div className="relative">
            <PromptInput
              prompt={prompt}
              isChatLoading={isChatLoading}
              handleSubmit={(e, file, activeTool, prompt) =>
                handleSubmit(e, file, activeTool, prompt, activeTool)
              }
              onPromptChange={handlePromptChange}
              onFileChange={setFile}
              file={file}
              activeTool={activeTool}
              setActiveTool={setActiveTool}
              stopGenerating={handleStop}
              subscription={subscription}
              selectedModel={selectedModel}
              models={models}
            />
          </div>
        )}
      </div>

      {/* Right Panel */}
      <RightPanel
        isExpanded={rightPanelExpanded}
        onToggle={setRightPanelExpanded}
        activePanel={activeRightPanel}
        onPanelChange={setActiveRightPanel}
        // Model settings props
        temperature={temperature}
        topP={topP}
        instructions={instructions}
        outputLength={outputLength}
        onTemperatureChange={setTemperature}
        onTopPChange={setTopP}
        onInstructionsChange={setInstructions}
        onOutputLengthChange={setOutputLength}
        onModelReset={handleModelReset}
        // YouTube tool props
        onTranscribe={handleYoutubeTranscribe}
        onSummarize={handleYoutubeSummarize}
        isYoutubeProcessing={false} // TODO: Add processing state
        // YouTube settings props
        youtubeActiveTab={youtubeActiveTab}
        youtubeSummaryType={youtubeSummaryType}
        youtubeLanguage={youtubeLanguage}
        youtubeTone={youtubeTone}
        onYoutubeActiveTabChange={handleYoutubeActiveTabChange}
        onYoutubeSummaryTypeChange={handleYoutubeSummaryTypeChange}
        onYoutubeLanguageChange={handleYoutubeLanguageChange}
        onYoutubeToneChange={handleYoutubeToneChange}
        // Deep Research props
        onResearch={handleResearch}
        isResearchProcessing={isResearchProcessing}
        researchMode={researchMode}
        citationStyle={citationStyle}
        includeWebSources={includeWebSources}
        includeDocSources={includeDocSources}
        includeAIReasoning={includeAIReasoning}
        onResearchModeChange={handleResearchModeChange}
        onCitationStyleChange={handleCitationStyleChange}
        onIncludeWebSourcesChange={handleIncludeWebSourcesChange}
        onIncludeDocSourcesChange={handleIncludeDocSourcesChange}
        onIncludeAIReasoningChange={handleIncludeAIReasoningChange}
        references={allReferences}
        onSaveReference={handleSaveReference}
      />

      {/* Plan upgrade modal removed per new flow */}
      {/* Error Modal for thread creation errors */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorModalMessage}
      />
      {/* Image Modal for larger preview */}
      <ImageModal
        isOpen={showImageModal}
        imageSrc={modalImageSrc}
        imageAlt={modalImageAlt}
        onClose={handleCloseImageModal}
      />

      {/* Upgrade Prompt for anonymous users */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        messageCount={messageCount}
        limit={dailyLimit}
        hoursUntilReset={hoursUntilReset}
      />
    </div>
  );
};

export default Chat;
